export interface PaymentBankConfig {
  paymentProvider: number;
  productType: string;
  bankCode: string;
  isRequireConsent: boolean;
  termsUrl?: string;
  termsVersion?: string;
}

export interface MqrConsentResponse {
  errorMessage: string | null;
  iframeUrl: string | null;
  isConsentApproved: boolean;
  isError: boolean;
  status: number;
}

const DEFAULT_MQR_CONSENT_ERROR: MqrConsentResponse = {
  errorMessage: 'Không đọc được phản hồi consent',
  iframeUrl: null,
  isConsentApproved: false,
  isError: true,
  status: -1,
};

const parseBooleanField = (value: unknown): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  }

  return false;
};

const unwrapMqrConsentRecord = (
  data: unknown,
): Record<string, unknown> | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const record = data as Record<string, unknown>;

  if (record.result && typeof record.result === 'object') {
    return record.result as Record<string, unknown>;
  }

  return record;
};

const readMqrConsentField = (
  record: Record<string, unknown>,
  camelKey: string,
  pascalKey: string,
): unknown => record[camelKey] ?? record[pascalKey];

export const normalizeMqrConsentResponse = (
  data: unknown,
): MqrConsentResponse => {
  if (typeof data === 'string') {
    try {
      return normalizeMqrConsentResponse(JSON.parse(data));
    } catch {
      return DEFAULT_MQR_CONSENT_ERROR;
    }
  }

  const record = unwrapMqrConsentRecord(data);
  if (!record) {
    return DEFAULT_MQR_CONSENT_ERROR;
  }

  const errorMessage = readMqrConsentField(
    record,
    'errorMessage',
    'ErrorMessage',
  );
  const iframeUrl = readMqrConsentField(record, 'iframeUrl', 'IframeUrl');
  const isConsentApproved = readMqrConsentField(
    record,
    'isConsentApproved',
    'IsConsentApproved',
  );
  const isError = readMqrConsentField(record, 'isError', 'IsError');
  const status = readMqrConsentField(record, 'status', 'Status');

  return {
    errorMessage: typeof errorMessage === 'string' ? errorMessage : null,
    iframeUrl: typeof iframeUrl === 'string' ? iframeUrl : null,
    isConsentApproved: parseBooleanField(isConsentApproved),
    isError: parseBooleanField(isError),
    status: typeof status === 'number' ? status : 0,
  };
};

export const getPaymentBankConfigItem = (
  configs: PaymentBankConfig[],
  paymentProvider?: number,
  bankCode?: string,
): PaymentBankConfig | undefined => {
  if (
    paymentProvider === undefined ||
    paymentProvider === null ||
    !bankCode
  ) {
    return undefined;
  }

  return configs.find(
    (item) =>
      item.paymentProvider === paymentProvider && item.bankCode === bankCode,
  );
};

export const isBankRequireConsent = (
  configs: PaymentBankConfig[],
  paymentProvider?: number,
  bankCode?: string,
): boolean => Boolean(getPaymentBankConfigItem(configs, paymentProvider, bankCode)?.isRequireConsent);

export interface BankOption {
  value: number;
  label: string;
}

export interface BankCodeOption {
  value: string;
  label: string;
}

export const normalizePaymentBankConfigs = (
  data: unknown,
): PaymentBankConfig[] => {
  if (Array.isArray(data)) {
    return data;
  }
  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;
    if (Array.isArray(record.items)) {
      return record.items as PaymentBankConfig[];
    }
    if (typeof record.productType === "string") {
      return [data as PaymentBankConfig];
    }
  }
  return [];
};

export const getPaymentProviderOptions = (
  configs: PaymentBankConfig[],
): BankOption[] => {
  const map = new Map<number, string>();
  configs.forEach((item) => {
    if (!map.has(item.paymentProvider)) {
      map.set(item.paymentProvider, item.productType);
    }
  });

  return Array.from(map.entries()).map(([value, label]) => ({
    value,
    label,
  }));
};

export const getBankOptionsByPaymentProvider = (
  configs: PaymentBankConfig[],
  paymentProvider?: number,
  selectedCode?: string,
): BankCodeOption[] => {
  if (paymentProvider === undefined || paymentProvider === null) {
    return [];
  }

  const options = configs
    .filter((item) => item.paymentProvider === paymentProvider)
    .map((item) => ({
      value: item.bankCode,
      label: item.bankCode,
    }));

  if (selectedCode && !options.some((item) => item.value === selectedCode)) {
    return [{ value: selectedCode, label: selectedCode }, ...options];
  }

  return options;
};

export interface BankFormValue {
  paymentProvider?: number;
  code?: string;
  bankAccount?: string;
  accountName?: string;
  isDefault?: boolean;
  inputChange?: string;
}

export const mapBankItemToForm = (
  item: Record<string, unknown>,
): BankFormValue => ({
  paymentProvider:
    typeof item.paymentProvider === 'number'
      ? item.paymentProvider
      : undefined,
  code: String(item.code ?? ''),
  bankAccount: String(item.bankAccount ?? ''),
  accountName: String(item.accountName ?? ''),
  isDefault: Boolean(item.isDefault),
});

export const mapBankFormToPayload = (item: BankFormValue) => ({
  paymentProvider: item.paymentProvider ?? 0,
  code: item.code ?? '',
  bankAccount: item.bankAccount ?? '',
  accountName: item.accountName ?? '',
  isDefault: item.isDefault ?? false,
});

export const mapBankListItem = <T extends Record<string, unknown>>(item: T) => ({
  ...item,
  paymentProvider: item.paymentProvider,
});
